import { createContext, useContext, useState, useMemo, type JSX } from "react";
import useLocalStorage from "app/custom-hooks/useLocalStorage";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type EducationBarProminentData = {
    isDismissed: boolean;
    handleDismiss: VoidFunction;
    isMinimized: boolean;
    handleMinimize: VoidFunction;
    handleMaximize: VoidFunction;
    isProminentEducationBannerShown: boolean;
};

const EducationBarProminentContext = createContext<EducationBarProminentData>({
    handleDismiss: undefined,
    handleMaximize: undefined,
    handleMinimize: undefined,
    isDismissed: false,
    isMinimized: false,
    isProminentEducationBannerShown: false,
});

export const EducationBarProminentProvider = ({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element => {
    const [isEducationBannerDismissed, setIsEducationBannerDismissed] = useLocalStorage("isEducationBannerDismissed");
    const [isDismissed, setIsDismissed] = useState(isEducationBannerDismissed === "true");
    const [isMinimized, setIsMinimized] = useState(isEducationBannerDismissed === "true");

    const handleDismiss = () => {
        setIsDismissed(true);
        setIsEducationBannerDismissed("true");
    };

    const handleMinimize = () => {
        setIsMinimized(true);
    };

    const handleMaximize = () => {
        setIsMinimized(false);
    };

    const { name } = proNavigator.current();
    const isProminentEducationBannerShown = name === "digitalsuite_website_websiteperformance";

    const providerValue = useMemo(
        () => ({
            isDismissed,
            handleDismiss,
            isMinimized,
            handleMinimize,
            handleMaximize,
            isProminentEducationBannerShown,
        }),
        [isDismissed, handleDismiss, isMinimized, handleMinimize, handleMaximize, isProminentEducationBannerShown],
    );

    return (
        <EducationBarProminentContext.Provider value={providerValue}>{children}</EducationBarProminentContext.Provider>
    );
};

export const useEducationBarProminentContext = () => useContext(EducationBarProminentContext);
